import React, { useCallback, useContext } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { buildSDPSearchPayload } from "app/utils/smartDPUtils";
import { useNavigate } from "react-router-dom";
import { SDP_HERO_TEXT_ALIGN } from "app/constants";
import "./SmartDPImmersiveSearch.scss";
import SmartDPSearchFormContainer from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchFormContainer";
import Paragraphs from "app/pages/.shared/Paragraphs";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const IMG_SIZES = {
	medium: { width: 978 },
	small: { width: 768 },
	large: { width: 1847 },
	xlarge: { width: 1847 },
};

const SmartDPImmersiveSearch = ({
	heroTextAlign,
	heroTitleDesktop,
	heroTitleMobile,
	heroSubtitleDesktop,
	heroSubtitleMobile,
	heroImageMobile,
	heroImageDesktop,
	heroImageMobileThumbnail,
	heroImageDesktopThumbnail,
	resetAllSDPProductsFilter = () => {},
	hideSmartDPForm = false,
}) => {
	const navigate = useNavigate();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const title = isMobile ? heroTitleMobile : heroTitleDesktop;
	const subtitle = isMobile ? heroSubtitleMobile : heroSubtitleDesktop;

	const backgroundImageUrl = isMobile ? heroImageMobile : heroImageDesktop;
	const backgroundImageThumbnail = isMobile
		? heroImageMobileThumbnail
		: heroImageDesktopThumbnail;

	let align = "";
	if (heroTextAlign === SDP_HERO_TEXT_ALIGN.LEFT) {
		align = "flex-start";
	} else if (heroTextAlign === SDP_HERO_TEXT_ALIGN.RIGHT) {
		align = "flex-end";
	}
	const justify = hideSmartDPForm ? "flex-end" : "center";

	const handleFormSubmission = useCallback(data => {
		const queryParams = buildSDPSearchPayload(data);

		resetAllSDPProductsFilter();

		navigate({ pathname: "/sdp/listing", search: queryParams });
	}, []);

	const marketingClass = classNames({
		"smartdp-immersive-search__marketing-container": true,
		"smartdp-immersive-search__marketing-container--without-form": hideSmartDPForm,
	});

	const sdpHeroClass = classNames({
		"smartdp-immersive-search__hero": true,
		"smartdp-immersive-search__hero--without-form": hideSmartDPForm,
	});
	return (
		<div className="smartdp-immersive-search">
			<div className={sdpHeroClass}>
				<ProgressiveImg
					sizes={IMG_SIZES}
					resolution={resolution}
					backgroundPosition={"top center"}
					thumbnail={backgroundImageThumbnail}
					src={backgroundImageUrl}
					cover
					fetchPriority="high"
					disableLazyLoad
				/>
				<div
					className="smartdp-immersive-search__hero-content"
					style={{
						justifyContent: justify,
					}}
				>
					<div className="smartdp-immersive-search__marketing">
						<h1
							className={marketingClass}
							style={{
								alignItems: align,
							}}
						>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL4}
								variantMD={TYPOGRAPHY_VARIANTS.XL6}
								isBold
								component="div"
							>
								<Paragraphs paragraphs={title} enableHTMLFormatting />
							</Typography>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								variantMD={TYPOGRAPHY_VARIANTS.XL3}
								isBold
								className="smartdp-immersive-search__subtitle"
								component="div"
							>
								<Paragraphs paragraphs={subtitle} enableHTMLFormatting />
							</Typography>
						</h1>
					</div>
					{!isMobile && !hideSmartDPForm && (
						<div className="smartdp-immersive-search__form">
							<SmartDPSearchFormContainer onSuccess={handleFormSubmission} />
						</div>
					)}
				</div>
			</div>
			{isMobile && !hideSmartDPForm && (
				<div className="smartdp-immersive-search__form">
					<SmartDPSearchFormContainer onSuccess={handleFormSubmission} />
				</div>
			)}
		</div>
	);
};

SmartDPImmersiveSearch.propTypes = {
	heroTextAlign: PropTypes.string,
	heroTitleDesktop: PropTypes.array,
	heroTitleMobile: PropTypes.array,
	heroSubtitleDesktop: PropTypes.array,
	heroSubtitleMobile: PropTypes.array,
	heroImageMobile: PropTypes.string,
	heroImageDesktop: PropTypes.string,
	heroImageMobileThumbnail: PropTypes.string,
	heroImageDesktopThumbnail: PropTypes.string,
	resetAllSDPProductsFilter: PropTypes.func,
	hideSmartDPForm: PropTypes.bool,
};

export default React.memo(SmartDPImmersiveSearch);
